import { GeoJsonLayer, IconLayer } from "@deck.gl/layers";
import { FeatureCollection } from "geojson";

import TimeRange from "@/types/time-range";
import {
  api,
  PublishedDashboard,
  StaticOverlayMapLayer as StaticLayerConfig,
} from "@/lib/api";
import MarkerIcon from "@/assets/images/map-pin.png";
import { DimensionValueSet } from "@/lib/dimensions";
import Layer from "@/lib/map-layers/layer";

export default class StaticLayer implements Layer {
  private config;
  private selectedDashboard;

  constructor(config: StaticLayerConfig, selectedDashboard: PublishedDashboard) {
    this.config = config;
    this.selectedDashboard = selectedDashboard;
  }

  get id() {
    return this.config.id;
  }
  get settings() {
    return [];
  }
  get dimensions() {
    return undefined;
  }

  private data: FeatureCollection | undefined;

  getRelevantDimensionValues(): DimensionValueSet {
    return {};
  }

  async initialize(timeRange: TimeRange) {
    try {
      const promise = await api.published.getPublishedMapLayerData(
        this.selectedDashboard.id,
        this.id,
        timeRange.timeStart.toString(),
        timeRange.timeEnd.toString(),
      );
      this.data = promise.data as unknown as FeatureCollection;
    } catch (error) {
      this.data = undefined;
      throw error;
    }
  }

  getTitle() {
    return this.config.title;
  }

  isActive() {
    return true;
  }

  isForecast() {
    return false;
  }

  isLoaded() {
    return this.data !== undefined;
  }

  hasLoadError(_dimensionValues: DimensionValueSet) {
    return false;
  }

  hasPartialLoadErrors() {
    return false;
  }

  clearLoadFailures() {
    // no-op
  }

  getColor(): [number, number, number] {
    switch (this.config.color) {
      case "black":
        return [0, 0, 0];
      case "grey":
        return [200, 200, 200];
      case "white":
      default:
        return [255, 255, 255];
    }
  }

  async preload() {
    return;
  }

  async loadLegendImage() {
    return undefined;
  }

  renderLegend() {
    return undefined;
  }

  renderLayer() {
    if (!this.isActive) return undefined;

    if (this.data?.features?.[0].geometry.type === "Point") {
      return new IconLayer({
        id: `icon-layer-${this.id}`,
        slot: "middle",

        data: this.data.features,
        getPosition: (d) => [
          Number(d.geometry.coordinates[0]),
          Number(d.geometry.coordinates[1]),
        ],
        getIcon: () => "marker",
        getSize: () => 32,
        getColor: () => this.getColor(),
        iconAtlas: MarkerIcon,
        iconMapping: {
          marker: {
            x: 0,
            y: 0,
            height: 64,
            width: 64,
            anchorX: 32,
            anchorY: 64,
            mask: true,
          },
        },
      });
    }

    return new GeoJsonLayer({
      id: `static-layer-${this.id}`,
      slot: "middle",

      data: this.data,
      filled: false,
      extruded: false,
      stroked: true,

      lineWidthMinPixels: 2,
      getLineColor: () => this.getColor(),
    });
  }

  renderComponents() {
    return undefined;
  }
}
